
import { useEffect } from "react";
import { useNavigate, Outlet, Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Sidebar, SidebarContent, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarProvider, SidebarTrigger, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenuSub, SidebarMenuSubItem, SidebarMenuSubButton, SidebarSeparator } from "@/components/ui/sidebar";
import { LogOut, LayoutDashboard, FileText, Users, FileCode, Send, Mail, TestTube, BarChart3, ListChecks, FolderKanban, Layers, Settings, ChevronDown, GitBranch } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ProjectSelector } from "@/components/ProjectSelector";
import { ProjectContextInitializer } from "@/components/ProjectContextInitializer";
import { useProject } from "@/hooks/useProject";

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectConfig } = useProject();

  // Helper function to check if a path is active
  const isActive = (path: string) => {
    if (path === "/admin" && location.pathname === "/admin") {
      return true;
    }
    return location.pathname.startsWith(path) && path !== "/admin";
  };

  // Active item color style
  const activeColor = "#00A8D3";

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth/login");
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth/login");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth/login");
  };

  return (
    <SidebarProvider defaultOpen>
      <div className="flex min-h-screen w-full">
        <Sidebar>
          <SidebarContent className="pl-4 p-2 flex flex-col justify-between h-full px-[20px]">
            <div className="flex flex-col justify-center h-full">
              <div className="flex justify-start mb-6">
                <img src="/lovable-uploads/b567a654-97e5-4401-9513-2574e8dbf7f0.png" alt="Líder 3m Logo" className="w-20 h-auto" />
              </div>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin" style={{ color: isActive("/admin") ? activeColor : "" }}>
                      <LayoutDashboard style={{ color: isActive("/admin") ? activeColor : "" }} />
                      <span>Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/clients" style={{ color: isActive("/admin/clients") ? activeColor : "" }}>
                      <Users style={{ color: isActive("/admin/clients") ? activeColor : "" }} />
                      <span>Clientes</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/forms" style={{ color: isActive("/admin/forms") ? activeColor : "" }}>
                      <ListChecks style={{ color: isActive("/admin/forms") ? activeColor : "" }} />
                      <span>Formularios</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/templates" style={{ color: isActive("/admin/templates") ? activeColor : "" }}>
                      <FileCode style={{ color: isActive("/admin/templates") ? activeColor : "" }} />
                      <span>Plantillas</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/launches" style={{ color: isActive("/admin/launches") ? activeColor : "" }}>
                      <Send style={{ color: isActive("/admin/launches") ? activeColor : "" }} />
                      <span>Lanzamientos</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/submissions" style={{ color: isActive("/admin/submissions") ? activeColor : "" }}>
                      <FileText style={{ color: isActive("/admin/submissions") ? activeColor : "" }} />
                      <span>Envíos</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/referrals-funnel" style={{ color: isActive("/admin/referrals-funnel") ? activeColor : "" }}>
                      <GitBranch style={{ color: isActive("/admin/referrals-funnel") ? activeColor : "" }} />
                      <span>Embudo Referidos</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/bulk-send" style={{ color: isActive("/admin/bulk-send") ? activeColor : "" }}>
                      <Mail style={{ color: isActive("/admin/bulk-send") ? activeColor : "" }} />
                      <span>Envío Masivo</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/admin/informations" style={{ color: isActive("/admin/informations") ? activeColor : "" }}>
                      <BarChart3 style={{ color: isActive("/admin/informations") ? activeColor : "" }} />
                      <span>Informes</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarSeparator className="my-4" />
                <Collapsible defaultOpen className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton>
                        <Settings />
                        <span>Configuración</span>
                        <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton asChild>
                            <Link to="/admin/users" style={{ color: isActive("/admin/users") ? activeColor : "" }}>
                              <Users style={{ color: isActive("/admin/users") ? activeColor : "" }} />
                              <span>Usuarios</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton asChild>
                            <Link to="/admin/projects" style={{ color: isActive("/admin/projects") ? activeColor : "" }}>
                              <FolderKanban style={{ color: isActive("/admin/projects") ? activeColor : "" }} />
                              <span>Proyectos</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                    <LogOut className="text-red-500" />
                    <span>Cerrar sesión</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </div>
            <SidebarFooter className="flex flex-col items-start mt-auto pt-4">
              <img src="https://crescente.es/wp-content/uploads/2025/03/cropped-icono-crescente-2.png" alt="Crescente Logo" className="h-auto w-auto max-h-8 object-contain opacity-15 filter grayscale" />
              <p className="text-xs text-gray-300 truncate mt-2">3M | Crescente © 2025</p>
            </SidebarFooter>
          </SidebarContent>
        </Sidebar>
        <main className="flex-1 p-6">
          <ProjectContextInitializer />
          <div className="flex items-center justify-between mb-6">
            <SidebarTrigger />
            <ProjectSelector />
          </div>
          <Outlet />
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminLayout;
